// Area codes with a hero photo in public/images/backgrounds/locations/<prefix>.jpg.
// Most area-code pages have none, and a missing file under /images/ is a real 404,
// so pages only reference a photo that exists. locationPhotos.test.js fails when
// this list and the folder disagree.
export const LOCATION_PHOTO_PREFIXES = new Set([
  '01138', '01144', '01157', '01163', '01164', '01172', '01183', '01184', '01202', '01206',
  '01212', '01213', '01223', '01224', '01227', '01253', '01273', '01302', '01312', '01382',
  '01392', '01412', '01432', '01473', '01482', '01515', '01522', '01603', '01604', '01613',
  '01617', '01733', '01752', '01792', '01793', '01823', '01865', '01908', '01916', '01942',
  '02031', '02079', '02393', '02477', '02895', '02921'
])

export const locationPhotoPath = prefix => (
  LOCATION_PHOTO_PREFIXES.has(prefix) ? `/images/backgrounds/locations/${prefix}.jpg` : null
)

// Narrower copies of each photo for narrow screens: <prefix>-<width>w.jpg (and
// .avif, .webp), the centre of the 2560x400 photo at its full 400 px height.
// The hero is background-size: cover at 50% 50%, so while the hero box is no
// wider in proportion than the copy, the browser scales by height and shows
// exactly the part of the photo the full one shows. Each width is wider in
// proportion than the hero box gets at or below its breakpoint on any location
// page, with room to spare for smaller text or a shorter fallback font, so a
// change to the hero's height or padding means checking these again. Widths keep
// the centre offset a multiple of 16 px, so the JPEG crop is lossless.
export const LOCATION_PHOTO_CROPS = [
  { maxViewport: 767, width: 1408 },
  { maxViewport: 430, width: 928 }
]

// The JPEG for any browser, then the AVIF and WebP copies beside it for browsers
// that read image-set() with type(). A declaration a browser cannot parse is
// dropped, so each falls back to the last one it understood.
const backgroundDeclarations = jpg => {
  const set = `url('${jpg.replace(/\.jpg$/, '.avif')}') type('image/avif'), url('${jpg.replace(/\.jpg$/, '.webp')}') type('image/webp'), url('${jpg}') type('image/jpeg')`
  return `background-image: url('${jpg}'); background-image: -webkit-image-set(${set}); background-image: image-set(${set});`
}

// The hero's photo rules for the given selector: the full photo, then each
// narrower copy under its breakpoint, narrowest last so it wins where both apply.
// A browser fetches only the image of the rule that applies.
export const locationPhotoBackground = (prefix, selector) => {
  const jpg = locationPhotoPath(prefix)
  if (!jpg) return null
  return [`${selector} { ${backgroundDeclarations(jpg)} }`]
    .concat(LOCATION_PHOTO_CROPS.map(({ maxViewport, width }) =>
      `@media (max-width: ${maxViewport}px) { ${selector} { ${backgroundDeclarations(jpg.replace(/\.jpg$/, `-${width}w.jpg`))} } }`))
    .join('\n')
}

// One preload per width band for the AVIF the rules above pick there, so the
// browser fetches the photo alongside the stylesheet instead of after it has
// applied. Only the AVIF is preloaded: a browser without AVIF skips a preload
// typed image/avif, and one with it takes the AVIF from the image-set.
export const locationPhotoPreloads = prefix => {
  const jpg = locationPhotoPath(prefix)
  if (!jpg) return []
  const avif = jpg.replace(/\.jpg$/, '.avif')
  return [{ href: avif, media: `(min-width: ${LOCATION_PHOTO_CROPS[0].maxViewport + 1}px)` }]
    .concat(LOCATION_PHOTO_CROPS.map(({ maxViewport, width }, i) => {
      const narrower = LOCATION_PHOTO_CROPS[i + 1]
      return {
        href: avif.replace(/\.avif$/, `-${width}w.avif`),
        media: narrower ? `(min-width: ${narrower.maxViewport + 1}px) and (max-width: ${maxViewport}px)` : `(max-width: ${maxViewport}px)`
      }
    }))
}
